import { useEffect, useState } from 'react';
import type { InsuranceTariff, ServiceProductOffer } from './types';

type Props = {
  offer: ServiceProductOffer;
  pending: boolean;
  onSave: (offer: ServiceProductOffer) => void;
};

function cloneOffer(offer: ServiceProductOffer): ServiceProductOffer {
  return JSON.parse(JSON.stringify(offer)) as ServiceProductOffer;
}

export function InsuranceOfferForm({ offer, pending, onSave }: Props) {
  const [draft, setDraft] = useState(() => cloneOffer(offer));

  useEffect(() => {
    setDraft(cloneOffer(offer));
  }, [offer]);

  function patchTariff(code: string, patch: Partial<InsuranceTariff>) {
    setDraft((prev) => ({
      ...prev,
      tariffs: prev.tariffs.map((tariff) => (tariff.code === code ? { ...tariff, ...patch } : tariff)),
    }));
  }

  function patchCell(code: string, rowIndex: number, colIndex: number, value: string) {
    setDraft((prev) => ({
      ...prev,
      tariffs: prev.tariffs.map((tariff) => {
        if (tariff.code !== code || !tariff.table) return tariff;
        const rows = tariff.table.rows.map((row, index) =>
          index === rowIndex ? row.map((cell, cellIndex) => (cellIndex === colIndex ? value : cell)) : row,
        );
        return { ...tariff, table: { ...tariff.table, rows } };
      }),
    }));
  }

  function patchColumn(code: string, colIndex: number, value: string) {
    setDraft((prev) => ({
      ...prev,
      tariffs: prev.tariffs.map((tariff) => {
        if (tariff.code !== code || !tariff.table) return tariff;
        const columns = tariff.table.columns.map((column, index) => (index === colIndex ? value : column));
        return { ...tariff, table: { ...tariff.table, columns } };
      }),
    }));
  }

  return (
    <div className="space-y-4 rounded-lg border border-border p-4">
      <div>
        <h2 className="text-sm font-semibold">Ficha pública</h2>
        <p className="text-xs text-muted-foreground mt-1">
          Teléfono, texto legal y primas que ve la web. El asistente de licencias usa el aviso de cada
          territorio: si cambias un precio, revisa también esa frase.
        </p>
      </div>
      <label className="block space-y-1">
        <span className="text-xs text-muted-foreground">WhatsApp (solo dígitos, con prefijo)</span>
        <input
          type="text"
          inputMode="numeric"
          value={draft.contactPhone}
          onChange={(e) => setDraft((prev) => ({ ...prev, contactPhone: e.target.value }))}
          className="w-48 h-10 rounded-md border border-input bg-background px-3 text-sm"
        />
      </label>
      <label className="block space-y-1">
        <span className="text-xs text-muted-foreground">Texto legal</span>
        <textarea
          value={draft.brokerLegal}
          onChange={(e) => setDraft((prev) => ({ ...prev, brokerLegal: e.target.value }))}
          rows={3}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        />
      </label>
      {draft.tariffs.map((tariff) => (
        <fieldset key={tariff.code} className="space-y-3 rounded-lg border border-border p-3">
          <legend className="px-1 text-sm font-semibold">{tariff.title}</legend>
          <label className="block space-y-1">
            <span className="text-xs text-muted-foreground">Título</span>
            <input
              type="text"
              value={tariff.title}
              onChange={(e) => patchTariff(tariff.code, { title: e.target.value })}
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-xs text-muted-foreground">Para qué sirve</span>
            <textarea
              value={tariff.summary}
              onChange={(e) => patchTariff(tariff.code, { summary: e.target.value })}
              rows={3}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-xs text-muted-foreground">Precio que se muestra</span>
            <input
              type="text"
              value={tariff.priceLabel}
              onChange={(e) => patchTariff(tariff.code, { priceLabel: e.target.value })}
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
            />
          </label>
          {tariff.priceCents != null && (
            <label className="block space-y-1">
              <span className="text-xs text-muted-foreground">Prima en céntimos</span>
              <input
                type="text"
                inputMode="numeric"
                value={String(tariff.priceCents)}
                onChange={(e) => {
                  const cents = Number(e.target.value.replace(',', '.'));
                  patchTariff(tariff.code, { priceCents: Number.isFinite(cents) ? Math.round(cents) : tariff.priceCents });
                }}
                className="w-32 h-10 rounded-md border border-input bg-background px-3 text-sm"
              />
            </label>
          )}
          {tariff.coverages != null && (
            <label className="block space-y-1">
              <span className="text-xs text-muted-foreground">Coberturas</span>
              <textarea
                value={tariff.coverages}
                onChange={(e) => patchTariff(tariff.code, { coverages: e.target.value })}
                rows={3}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </label>
          )}
          {tariff.whenRequired != null && (
            <label className="block space-y-1">
              <span className="text-xs text-muted-foreground">Cuándo es obligatorio</span>
              <textarea
                value={tariff.whenRequired}
                onChange={(e) => patchTariff(tariff.code, { whenRequired: e.target.value })}
                rows={2}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </label>
          )}
          {tariff.payment != null && (
            <label className="block space-y-1">
              <span className="text-xs text-muted-foreground">Cómo se paga</span>
              <textarea
                value={tariff.payment}
                onChange={(e) => patchTariff(tariff.code, { payment: e.target.value })}
                rows={2}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </label>
          )}
          <label className="block space-y-1">
            <span className="text-xs text-muted-foreground">Texto del WhatsApp</span>
            <input
              type="text"
              value={tariff.whatsappText}
              onChange={(e) => patchTariff(tariff.code, { whatsappText: e.target.value })}
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
            />
          </label>
          {tariff.table && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <caption className="text-left text-xs text-muted-foreground pb-2">{tariff.table.caption}</caption>
                <thead>
                  <tr>
                    {tariff.table.columns.map((column, colIndex) => (
                      <th key={colIndex} className="p-1 align-top">
                        <input
                          type="text"
                          value={column}
                          onChange={(e) => patchColumn(tariff.code, colIndex, e.target.value)}
                          className="w-full min-w-28 h-9 rounded-md border border-input bg-background px-2 text-xs"
                        />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tariff.table.rows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {row.map((cell, colIndex) => (
                        <td key={colIndex} className="p-1">
                          <input
                            type="text"
                            value={cell}
                            onChange={(e) => patchCell(tariff.code, rowIndex, colIndex, e.target.value)}
                            className="w-full min-w-28 h-9 rounded-md border border-input bg-background px-2 text-xs"
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {(tariff.notes || []).map((note, index) => (
            <label key={`note-${index}`} className="block space-y-1">
              <span className="text-xs text-muted-foreground">Nota</span>
              <textarea
                value={note}
                onChange={(e) => {
                  const notes = (tariff.notes || []).map((item, noteIndex) =>
                    noteIndex === index ? e.target.value : item,
                  );
                  patchTariff(tariff.code, { notes });
                }}
                rows={2}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </label>
          ))}
          {(tariff.reasons || []).map((reason, index) => (
            <label key={`reason-${index}`} className="block space-y-1">
              <span className="text-xs text-muted-foreground">Por qué tenerlo</span>
              <textarea
                value={reason}
                onChange={(e) => {
                  const reasons = (tariff.reasons || []).map((item, reasonIndex) =>
                    reasonIndex === index ? e.target.value : item,
                  );
                  patchTariff(tariff.code, { reasons });
                }}
                rows={2}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </label>
          ))}
          {(tariff.footnotes || []).map((note, index) => (
            <label key={`foot-${index}`} className="block space-y-1">
              <span className="text-xs text-muted-foreground">Pie de tabla</span>
              <textarea
                value={note}
                onChange={(e) => {
                  const footnotes = (tariff.footnotes || []).map((item, noteIndex) =>
                    noteIndex === index ? e.target.value : item,
                  );
                  patchTariff(tariff.code, { footnotes });
                }}
                rows={2}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </label>
          ))}
          {(tariff.requiredFor || []).map((req, index) => (
            <div key={`${req.ccaa}-${index}`} className="space-y-2 rounded-md bg-muted/40 p-3">
              <p className="text-xs font-medium">
                Aviso en el asistente · {req.ccaa} · {req.typeIds.join(', ')}
              </p>
              {(['noticeEs', 'noticeEn'] as const).map((lang) => (
                <div key={lang} className="grid gap-2">
                  <span className="text-xs text-muted-foreground">{lang === 'noticeEs' ? 'Castellano' : 'Inglés'}</span>
                  {(['before', 'phrase', 'after'] as const).map((part) => (
                    <input
                      key={part}
                      type="text"
                      value={req[lang][part]}
                      onChange={(e) => {
                        const requiredFor = (tariff.requiredFor || []).map((item, reqIndex) => {
                          if (reqIndex !== index) return item;
                          return {
                            ...item,
                            [lang]: { ...item[lang], [part]: e.target.value },
                          };
                        });
                        patchTariff(tariff.code, { requiredFor });
                      }}
                      className="w-full h-9 rounded-md border border-input bg-background px-2 text-xs"
                    />
                  ))}
                </div>
              ))}
            </div>
          ))}
        </fieldset>
      ))}
      <button
        type="button"
        disabled={pending}
        onClick={() => onSave(draft)}
        className="h-10 px-3 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
      >
        Guardar ficha
      </button>
    </div>
  );
}
